/**
 * @fileoverview Hooks for fetching Payments (Universal Transactions) with pagination.
 * @module features/payments/hooks/usePayments
 */

import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from '@tanstack/react-query'
import { listPayments } from '../api/paymentsService'
import type { PaymentsPageResponse } from '../schemas/paymentsSchema'

/**
 * Create month range [start, end] in YYYY-MM-DD for universal-transactions.
 * Note: Backend uses inclusive end (Between/<=), so we pass endOfMonth.
 * @param year The calendar year
 * @param monthIndex Zero-based month index (0=Jan)
 */
export function monthRange(year: number, monthIndex: number): { start: string; end: string } {
  const start = startOfMonth(new Date(year, monthIndex, 1))
  const end = endOfMonth(start)
  const fmt = (d: Date): string => formatISO(d, { representation: 'date' })
  return { start: fmt(start), end: fmt(end) }
}

/**
 * usePayments
 * Infinite query that pages by 50 rows within a selected month, direction=debit.
 */
export function usePayments(
  year: number,
  monthIndex: number,
  pageSize = 50,
  search?: string,
  sort = 'txnDate,asc'
): UseInfiniteQueryResult<InfiniteData<PaymentsPageResponse>, Error> {
  const { start, end } = monthRange(year, monthIndex)
  return useInfiniteQuery<PaymentsPageResponse, Error>({
    queryKey: ['payments', year, monthIndex, pageSize, start, end, 'debit', sort, search],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PaymentsPageResponse> => {
      const page = typeof pageParam === 'number' ? pageParam : 0
      return listPayments({
        startDate: start,
        endDate: end,
        page,
        size: pageSize,
        sort,
        direction: 'debit',
        ...(search ? { search } : {}),
      })
    },
    getNextPageParam: lastPage => {
      // Prefer Spring-style metadata when present
      if (typeof lastPage.number === 'number' && typeof lastPage.totalPages === 'number') {
        const next = lastPage.number + 1
        return next < lastPage.totalPages ? next : undefined
      }
      // Fallback: if we received a full page, try next index
      const nextIndex = lastPage.size ?? pageSize
      return lastPage.content.length < nextIndex
        ? undefined
        : typeof lastPage.number === 'number'
          ? lastPage.number + 1
          : 1
    },
  }) as unknown as UseInfiniteQueryResult<InfiniteData<PaymentsPageResponse>, Error>
}

/**
 * useAllTransactions
 * Infinite query that pages by 50 rows within a selected month, fetching all transaction directions.
 */
export function useAllTransactions(
  year: number,
  monthIndex: number,
  pageSize = 50,
  search?: string,
  sort = 'txnDate,asc'
): UseInfiniteQueryResult<InfiniteData<PaymentsPageResponse>, Error> {
  const { start, end } = monthRange(year, monthIndex)
  return useInfiniteQuery<PaymentsPageResponse, Error>({
    queryKey: ['payments', year, monthIndex, pageSize, start, end, 'all', sort, search],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<PaymentsPageResponse> => {
      const page = typeof pageParam === 'number' ? pageParam : 0
      return listPayments({
        startDate: start,
        endDate: end,
        page,
        size: pageSize,
        sort,
        ...(search ? { search } : {}),
        // Omit direction to get all transactions
      })
    },
    getNextPageParam: lastPage => {
      // Prefer Spring-style metadata when present
      if (typeof lastPage.number === 'number' && typeof lastPage.totalPages === 'number') {
        const next = lastPage.number + 1
        return next < lastPage.totalPages ? next : undefined
      }
      // Fallback: if we received a full page, try next index
      const nextIndex = lastPage.size ?? pageSize
      return lastPage.content.length < nextIndex
        ? undefined
        : typeof lastPage.number === 'number'
          ? lastPage.number + 1
          : 1
    },
  }) as unknown as UseInfiniteQueryResult<InfiniteData<PaymentsPageResponse>, Error>
}
